import { signal } from '@angular/core';

/** Localization keys per field name. */
export type FormFieldErrorKeyMap = Record<string, string>;

export function isFormValueEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim().length === 0;
  }
  if (typeof value === 'number') {
    return Number.isNaN(value);
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
}

export interface FormFieldValidationRule {
  /** Stable field id used in templates (e.g. code, title). */
  field: string;
  messageKey: string;
  value?: () => unknown;
  /** When false, skip this rule. */
  when?: () => boolean;
  /** Custom validator; return true when valid. */
  isValid?: () => boolean;
}

/**
 * Shared client-side field error state for Admin forms (ngModel / template-driven).
 * Store i18n keys; resolve with {@link FormFieldErrorsState.errorText} in templates.
 */
export class FormFieldErrorsState {
  readonly keys = signal<FormFieldErrorKeyMap>({});

  has(field: string): boolean {
    return !!this.keys()[field];
  }

  errorKey(field: string): string {
    return this.keys()[field] ?? '';
  }

  errorText(field: string, translate: (key: string) => string): string {
    const key = this.errorKey(field);
    return key ? translate(key) : '';
  }

  clear(field: string): void {
    const current = this.keys();
    if (!current[field]) {
      return;
    }
    const next = { ...current };
    delete next[field];
    this.keys.set(next);
  }

  clearAll(): void {
    if (Object.keys(this.keys()).length === 0) {
      return;
    }
    this.keys.set({});
  }

  setField(field: string, messageKey: string): void {
    this.keys.update(current => ({ ...current, [field]: messageKey }));
  }

  /**
   * Runs rules, updates field keys, returns true when all rules pass.
   */
  validate(rules: FormFieldValidationRule[]): boolean {
    const next: FormFieldErrorKeyMap = {};
    for (const rule of rules) {
      if (rule.when && !rule.when()) {
        continue;
      }

      if (rule.isValid) {
        if (!rule.isValid()) {
          next[rule.field] = rule.messageKey;
        }
        continue;
      }

      const value = rule.value?.();
      if (isFormValueEmpty(value)) {
        next[rule.field] = rule.messageKey;
      }
    }

    this.keys.set(next);
    return Object.keys(next).length === 0;
  }
}
